import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FeatherIcon from './FeatherIcon';
import './DateRangeFilter.css';

/** Format a Date object as YYYY-MM-DD in local time (avoids UTC shift from toISOString) */
const toLocalDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// `hint` is the pill's native tooltip. The pills are two characters wide and
// the control carries no "Date Range" label, so each one says what it means
// on hover instead. `hint` (and Custom's label) are commonUi keys.
const PRESETS = [
  { key: '1d', label: '1D', days: 1, hint: 'hint1d' },
  { key: '7d', label: '7D', days: 7, hint: 'hint7d' },
  { key: '30d', label: '30D', days: 30, hint: 'hint30d' },
  { key: '90d', label: '90D', days: 90, hint: 'hint90d' },
  { key: 'custom', label: null, hint: 'hintCustom' },
];

/**
 * Convert a preset key to { start_time, end_time } ISO strings.
 * Returns null values for "all" / unresolvable keys.
 */
const presetToRange = (key) => {
  const preset = PRESETS.find((p) => p.key === key);
  if (!preset || !preset.days) return { start_time: null, end_time: null };
  const now = new Date();
  const start = new Date(now.getTime() - preset.days * 86_400_000);
  return {
    start_time: start.toISOString(),
    end_time: now.toISOString(),
  };
};

/**
 * Shared date-range filter used across Gemini Enterprise Agent pages.
 *
 * Props:
 *  - value        : current preset key ('1d','7d','30d','90d','custom')
 *  - onChange(key, { start_time, end_time }) : called when user selects a preset or applies custom range
 *  - allowCustom  : offer the Custom range (default true). False for pages whose
 *                   API takes only named windows, so the pill can't promise a
 *                   span the data cannot follow.
 */
const DateRangeFilter = ({ value = '30d', onChange, dateRange, allowCustom = true }) => {
  const { t, i18n } = useTranslation('commonUi');

  const [showCustom, setShowCustom] = useState(false);

  const [customStart, setCustomStart] = useState(() => {
    if (dateRange?.start_time) return toLocalDateStr(new Date(dateRange.start_time));
    return '';
  });

  const [customEnd, setCustomEnd] = useState(() => {
    if (dateRange?.end_time) return toLocalDateStr(new Date(dateRange.end_time));
    return '';
  });

  const containerRef = useRef(null);

  // Sync custom dates from parent dateRange prop (e.g. after remount)
  useEffect(() => {
    if (value === 'custom' && dateRange?.start_time && dateRange?.end_time) {
      setCustomStart(toLocalDateStr(new Date(dateRange.start_time)));
      setCustomEnd(toLocalDateStr(new Date(dateRange.end_time)));
    }
  }, [value, dateRange]);

  // Close popover on outside click (but not when clicking the pills themselves)
  useEffect(() => {
    if (!showCustom) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowCustom(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCustom]);

  const handlePreset = useCallback((key) => {
    if (key === 'custom') {
      setShowCustom((prev) => !prev);
      // Pre-fill custom inputs with sensible defaults only if empty
      if (!customStart || !customEnd) {
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (!customStart) setCustomStart(toLocalDateStr(weekAgo));
        if (!customEnd) setCustomEnd(toLocalDateStr(now));
      }
      return;
    }
    setShowCustom(false);
    const range = presetToRange(key);
    onChange(key, range);
  }, [onChange, customStart, customEnd]);

  const handleApplyCustom = useCallback(() => {
    if (!customStart || !customEnd) return;
    // Parse as local date (not UTC) to avoid timezone shift
    const [sy, sm, sd] = customStart.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
    const [ey, em, ed] = customEnd.split('-').map(Number);
    const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
    onChange('custom', {
      start_time: start.toISOString(),
      end_time: end.toISOString(),
    });
    setShowCustom(false);
  }, [customStart, customEnd, onChange]);

  // Format active range display for custom
  const formatCustomLabel = () => {
    if (value !== 'custom' || !customStart || !customEnd) return null;
    const fmt = (d) => {
      const [y, m, day] = d.split('-').map(Number);
      return new Date(y, m - 1, day).toLocaleDateString(i18n.language || undefined, { month: 'short', day: 'numeric' });
    };
    return `${fmt(customStart)} – ${fmt(customEnd)}`;
  };

  return (
    <div className="date-range-filter" style={{ position: 'relative' }} ref={containerRef}>
      <div className="drf-pills">
        {PRESETS.filter((p) => allowCustom || p.key !== 'custom').map((p) => (
          <button
            key={p.key}
            className={`drf-pill ${value === p.key ? 'active' : ''}`}
            /* `title` only — an aria-label here would REPLACE the visible
               label, so the button whose text reads "7D" would announce
               itself as "Last 7 days" and match neither what is on screen
               nor what anyone asks for. The tooltip is a description, not a
               new name. */
            title={t(`commonUi:commonDaterangefilter.${p.hint}`)}
            onClick={() => handlePreset(p.key)}
          >
            {p.key === 'custom' && (
              <FeatherIcon name="calendar" size={12} style={{ marginRight: 4, verticalAlign: '-1px' }} />
            )}
            {p.key === 'custom' && value === 'custom' && formatCustomLabel()
              ? formatCustomLabel()
              : (p.label || t('commonUi:commonDaterangefilter.custom'))}
          </button>
        ))}
      </div>

      {showCustom && (
        <div className="drf-custom-popover">
          <div className="drf-date-row">
            <span className="drf-date-label">{t('commonUi:commonDaterangefilter.from')}</span>
            <input
              type="date"
              className="drf-date-input"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              max={customEnd || undefined}
            />
          </div>
          <div className="drf-date-row">
            <span className="drf-date-label">{t('commonUi:commonDaterangefilter.to')}</span>
            <input
              type="date"
              className="drf-date-input"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              min={customStart || undefined}
              max={toLocalDateStr(new Date())}
            />
          </div>
          <div className="drf-apply-row">
            <button className="drf-apply-btn secondary" onClick={() => setShowCustom(false)}>{t('commonUi:commonDaterangefilter.cancel')}</button>
            <button
              className="drf-apply-btn primary"
              onClick={handleApplyCustom}
              disabled={!customStart || !customEnd}
            > {t('commonUi:commonDaterangefilter.apply')} </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { presetToRange };
export default DateRangeFilter;
